import { useMemo, useState } from "react";
import { Eye, EyeOff, Check, X as XIcon } from "lucide-react";

export function passwordStrength(pwd) {
  if (!pwd) return { score: 0, label: "", checks: defaultChecks(false) };
  const checks = {
    length: pwd.length >= 8,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    number: /[0-9]/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd),
  };
  const score = Object.values(checks).filter(Boolean).length;
  const labels = ["Very weak", "Weak", "Fair", "Good", "Strong", "Very strong"];
  return { score, label: labels[score], checks };
}

function defaultChecks(v) {
  return { length: v, upper: v, lower: v, number: v, special: v };
}

export function PasswordField({
  value,
  onChange,
  placeholder = "Choose a strong password",
  showStrength = true,
  testId = "password-input",
  required = true,
  minLength = 8,
  autoComplete = "new-password",
  label = "Password",
}) {
  const [show, setShow] = useState(false);
  const strength = useMemo(() => passwordStrength(value), [value]);

  const barColor = ["var(--te-divider)", "#DC2626", "#E8A341", "#E8A341", "#347B5A", "var(--te-primary)"];

  return (
    <div>
      <label className="te-label">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          minLength={minLength}
          autoComplete={autoComplete}
          className="te-input pr-12"
          placeholder={placeholder}
          data-testid={testId}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--te-text-muted)] hover:text-[var(--te-text)] p-1"
          aria-label={show ? "Hide password" : "Show password"}
          data-testid={`${testId}-toggle`}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>

      {showStrength && value && (
        <div className="mt-2.5">
          <div className="flex gap-1 h-1.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="flex-1 rounded-full transition-colors"
                style={{ background: strength.score >= i ? barColor[strength.score] : "var(--te-input-bg)" }}
              />
            ))}
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-[var(--te-text-secondary)]" data-testid="password-strength-label">
              Strength: <span className="font-semibold" style={{ color: barColor[strength.score] }}>{strength.label}</span>
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2">
            <Rule ok={strength.checks.length} text="8+ characters" />
            <Rule ok={strength.checks.upper} text="Uppercase (A-Z)" />
            <Rule ok={strength.checks.lower} text="Lowercase (a-z)" />
            <Rule ok={strength.checks.number} text="Number (0-9)" />
            <Rule ok={strength.checks.special} text="Symbol (!@#…)" />
          </div>
        </div>
      )}
    </div>
  );
}

function Rule({ ok, text }) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {ok ? <Check size={12} className="text-[#347B5A]" /> : <XIcon size={12} className="text-[var(--te-text-muted)]" />}
      <span className={ok ? "text-[var(--te-text)]" : "text-[var(--te-text-muted)]"}>{text}</span>
    </div>
  );
}
